import { useIntl } from '@edx/frontend-platform/i18n';
import { RiGraduationCapLine } from '@remixicon/react';
import { useNavigate } from 'react-router-dom';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

import {
  AiQueryBar,
  DashboardBanner,
  FeaturedCourseCard,
  FlashcardsSection,
  Greeting,
  OngoingCoursesSection,
  ScheduleCard,
} from './components';
import componentMessages from './components/messages';
import useDashboardState, { DASHBOARD_STATE } from './useDashboardState';
import messages from './messages';

import './index.scss';

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url || '');

/**
 * State-aware learner home surface. The page does not have a static layout —
 * it composes itself from the dashboard state derived in `useDashboardState`.
 *
 *  State A — Discovery:      greeting · marketing banner (hero) · AI prompt
 *  State B — Pre-Engagement: greeting · Start Learning hero (+ schedule) · AI prompt
 *  State C — Active Learning: greeting · Resume hero + schedule · flashcards ·
 *                             ongoing courses · marketing banner (bottom)
 */
const Dashboard = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const {
    status,
    state,
    name,
    banner,
    startCourse,
    resumeCourse,
    ongoingItems,
    hasUpcomingSession,
  } = useDashboardState();

  // getFeatureToggle is a hook (wraps useQuery) — must run before any early
  // return so hook order stays stable across renders. Learner AI (Ask-AI bar +
  // flashcards) is on by default; only an explicit `features_ai_learner ===
  // false` org row hides it.
  const featuresToggled = getFeatureToggle();
  // Hide while the toggle query loads to avoid a flash of AI UI that then
  // disappears for orgs that have it off; defaults on once resolved.
  const aiEnabled = !featuresToggled.loading && featuresToggled.features_ai_learner !== false;

  const goTo = (url) => {
    if (!url) { return; }
    if (isAbsoluteUrl(url)) {
      window.location.assign(url);
      return;
    }
    navigate(url);
  };

  // Until the enrollment signal resolves, the state is undetermined — render
  // only the greeting anchor so the page does not flicker between states.
  if (status === 'loading') {
    return (
      <div className="dashboard-page">
        <Greeting welcomeMessage="" name={name} />
      </div>
    );
  }

  const isDiscovery = state === DASHBOARD_STATE.DISCOVERY;
  const isPreEngagement = state === DASHBOARD_STATE.PRE_ENGAGEMENT;
  const isActive = state === DASHBOARD_STATE.ACTIVE;

  const greeting = intl.formatMessage(isActive ? messages.greeting : messages.greetingStart);

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero">
        <Greeting welcomeMessage={greeting} name={name} />

        {/* State A — marketing banner as the page hero. */}
        {isDiscovery && banner && <DashboardBanner banner={banner} />}

        {/* State B — Start Learning hero; schedule only if a session is upcoming. */}
        {isPreEngagement && startCourse && (
          <div className="dashboard-hero__row">
            <FeaturedCourseCard
              title={startCourse.title}
              progressPercent={0}
              courseBadgeText={intl.formatMessage(messages.courseBadge)}
              imageUrl={startCourse.imageUrl}
              badgeIconSrc={RiGraduationCapLine}
              ctaLabel={intl.formatMessage(componentMessages.startLearning)}
              onContinue={() => goTo(startCourse.homeUrl)}
            />
            {hasUpcomingSession && <ScheduleCard />}
          </div>
        )}

        {/* State C — Resume hero + schedule side by side. */}
        {isActive && (
          <div className="dashboard-hero__row">
            {resumeCourse && (
              <FeaturedCourseCard
                title={resumeCourse.title}
                progressPercent={resumeCourse.progressPercent}
                courseBadgeText={resumeCourse.badgeText}
                imageUrl={resumeCourse.imageUrl}
                badgeIconSrc={RiGraduationCapLine}
                ctaLabel={intl.formatMessage(componentMessages.continueLearning)}
                onContinue={() => goTo(resumeCourse.resumeUrl)}
              />
            )}
            <ScheduleCard />
          </div>
        )}
      </div>

      {/* State C — recap, browse, then discover. Each section self-hides when empty. */}
      {isActive && (
        <>
          {aiEnabled && <FlashcardsSection />}
          <OngoingCoursesSection items={ongoingItems} />
          {banner && <DashboardBanner banner={banner} />}
        </>
      )}

      {/* States A and B — AI tutor prompt as a secondary entry point. */}
      {(isDiscovery || isPreEngagement) && aiEnabled && (
        <AiQueryBar
          placeholder={intl.formatMessage(
            isDiscovery ? messages.aiQueryPlaceholder : messages.aiQueryPlaceholderAsk,
          )}
        />
      )}
    </div>
  );
};

export default Dashboard;
